import type { PermissionGroupDefinition } from '@genealogy-app/shared';
import apiClient from './client';

// ─── Mitglieder des aktiven Stammbaums ──────────────────────────────────────
//
// Alle Aufrufe beziehen sich auf den Baum, der über den X-Tree-Id-Header
// mitgeht – der Client setzt ihn selbst. Zugriff hat nur der Besitzer; der
// Server antwortet allen anderen mit 403.

export interface MemberRole {
  id: string;
  name: string;
  description?: string | null;
  permissions: string[];
  isSystem: boolean;
  /** true = eigene Rolle dieses Stammbaums, false = Vorlage des Betreibers. */
  eigene?: boolean;
}

export interface Member {
  userId: string;
  email: string;
  isActive: boolean;
  isOwner: boolean;
  joinedAt: string;
  role: MemberRole;
}

export interface AvailableRoles {
  data: MemberRole[];
  groups: PermissionGroupDefinition[];
  /** Rechte auf Gesundheitsdaten – in der Oberfläche gesondert kenntlich. */
  sensitive: string[];
}

/** Eine ausgesprochene Einladung, auf die noch geantwortet wird. */
export interface OffeneEinladung {
  id: string;
  email: string;
  rolleName: string;
  roleId: string;
  eingeladenAm: string;
  gueltigBis: string;
}

export interface Mitgliederstand {
  mitglieder: Member[];
  einladungen: OffeneEinladung[];
}

export async function listMembers(): Promise<Mitgliederstand> {
  const { data } = await apiClient.get<{ data: Member[]; einladungen: OffeneEinladung[] }>(
    '/members',
  );
  return { mitglieder: data.data, einladungen: data.einladungen ?? [] };
}

export interface EinladungAusgesprochen extends OffeneEinladung {
  /** Falsch heißt: die Einladung steht, aber es ging keine E-Mail hinaus. */
  benachrichtigt: boolean;
  fristTage: number;
}

/**
 * Lädt jemanden ein. Mitglied wird er erst, wenn er zustimmt.
 *
 * Hieß früher `addMember` und legte die Mitgliedschaft sofort an – wer eine
 * fremde Adresse kannte, konnte jeden ungefragt in seinen Stammbaum ziehen.
 */
export async function ladeEin(email: string, roleId: string): Promise<EinladungAusgesprochen> {
  const { data } = await apiClient.post<EinladungAusgesprochen>('/members', { email, roleId });
  return data;
}

/** Zieht eine Einladung zurück, die noch niemand beantwortet hat. */
export async function zieheEinladungZurueck(id: string): Promise<void> {
  await apiClient.delete(`/members/einladungen/${id}`);
}

export async function setMemberRole(userId: string, roleId: string): Promise<void> {
  await apiClient.patch(`/members/${userId}`, { roleId });
}

export async function removeMember(userId: string): Promise<void> {
  await apiClient.delete(`/members/${userId}`);
}

/** Übergibt den Stammbaum. Danach ist der Aufrufer gewöhnliches Mitglied. */
export async function transferOwnership(userId: string): Promise<{ newOwner: string }> {
  const { data } = await apiClient.post<{ newOwner: string }>(`/members/${userId}/transfer`);
  return data;
}

export async function fetchAvailableRoles(): Promise<AvailableRoles> {
  const { data } = await apiClient.get<AvailableRoles>('/members/roles/available');
  return data;
}

export interface RoleInput {
  name: string;
  description?: string | null;
  permissions: string[];
}

export async function createRole(input: RoleInput): Promise<MemberRole> {
  const { data } = await apiClient.post<MemberRole>('/members/roles', input);
  return data;
}

export async function updateRole(roleId: string, input: Partial<RoleInput>): Promise<MemberRole> {
  const { data } = await apiClient.patch<MemberRole>(`/members/roles/${roleId}`, input);
  return data;
}

export async function deleteRole(roleId: string): Promise<void> {
  await apiClient.delete(`/members/roles/${roleId}`);
}
